/**
 * A step renamed (Feature 71, module 4): its links, its position, every
 * reference to it, and every write into it follow, and a decision that
 * follows it or reads it too (Feature 79).
 */
import type { LayoutPoint, LinkageRules, LinkageSource } from '@optum/api-client/types';

import { FIRST, type Draft } from './draft.js';
import { isListFilter } from './lists.js';

const NAME = /^[a-z][a-z0-9_]{0,63}$/;

/** Why `to` may not be renamed to, or null when it may. */
export function renameRefusal(draft: Draft, from: string, to: string): string | null {
  if (to === from) return null;
  if (to === FIRST) return 'first is the completed task';
  if (!NAME.test(to)) return 'lowercase letters, digits and _, starting with a letter';
  if (draft.steps.some((s) => s.name === to)) return 'another step has this name';
  if (draft.decisions.some((d) => d.name === to)) return 'a gate node has this name';
  return null;
}

function renamedRef(ref: string, from: string, to: string): string {
  const prefix = `steps.${from}.`;
  return ref.startsWith(prefix) ? `steps.${to}.${ref.slice(prefix.length)}` : ref;
}

/** A source reading the renamed step; a list filter keeps its item conditions and pick, which read the item. */
function renamedSource(source: LinkageSource, from: string, to: string): LinkageSource {
  if (isListFilter(source)) return { ...source, ref: renamedRef(source.ref, from, to) };
  return 'ref' in source ? { ref: renamedRef(source.ref, from, to) } : source;
}

function renamedRules(rules: LinkageRules, from: string, to: string): LinkageRules {
  return { ...rules, items: rules.items.map((r) => ({ ...r, ref: renamedRef(r.ref, from, to) })) };
}

/** A step renamed, with its links, its position, every reference, and every write into it. */
export function renameStep(draft: Draft, from: string, to: string): Draft {
  if (renameRefusal(draft, from, to) !== null || from === to) return draft;
  const layout: Record<string, LayoutPoint> = {};
  for (const [name, point] of Object.entries(draft.layout))
    layout[name === from ? to : name] = point;
  return {
    ...draft,
    steps: draft.steps.map((s) => ({
      ...s,
      name: s.name === from ? to : s.name,
      mapping: Object.fromEntries(
        Object.entries(s.mapping).map(([field, source]) => [
          field,
          renamedSource(source, from, to),
        ]),
      ),
      ...(s.write_back && s.write_back.target === from
        ? { write_back: { ...s.write_back, target: to } }
        : {}),
    })),
    links: draft.links.map((l) => ({
      ...l,
      from: l.from === from ? to : l.from,
      to: l.to === from ? to : l.to,
      rules: renamedRules(l.rules, from, to),
    })),
    // A decision follows the renamed step, and its branches read it (Feature 79).
    decisions: draft.decisions.map((d) => ({
      ...d,
      from: d.from === from ? to : d.from,
      ...(d.ref !== undefined ? { ref: renamedRef(d.ref, from, to) } : {}),
      branches: d.branches.map((b) =>
        b.rules ? { ...b, rules: renamedRules(b.rules, from, to) } : b,
      ),
    })),
    layout,
  };
}
